import { useContext } from "react";
import Skeleton from "react-loading-skeleton";
import { PharmacyDetails, Grid, Icon, Paragraph } from "capitalrx-components-2.0";

import { QueryParams } from "@/types/QueryParams";

import PharmacyDetailsPriceCard from "../PharmacyDetailsPriceCard/PharmacyDetailsPriceCard";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";

import styles from "./PharmacyDetails.module.scss";
import { ThemeSelectorContext } from "@/context/index";

interface Pharmacy {
  pharmacyParams: QueryParams;
  pharmacyInfo: PharmacyDetails;
  isLoading: boolean;
}

const PharmacyDetailsComponent = (props: Pharmacy): JSX.Element => {
  const { pharmacyParams, pharmacyInfo, isLoading } = props;

  const formattedPhone = formatPhoneNumber(pharmacyInfo?.phone);

  const { client } = useContext(ThemeSelectorContext);
  const iconColor = client ? "lightgrey" : "turquoise";

  const PharmacyAddress = () => {
    return (
      <div data-testid="pharmacy-details-address">
        {pharmacyInfo?.address_line_1} <br />
        {pharmacyInfo?.address_line_2 ? (
          <>
            {pharmacyInfo.address_line_2}
            <br />
          </>
        ) : null}
        {pharmacyInfo?.city}, {pharmacyInfo?.state} {pharmacyInfo?.zip}
      </div>
    );
  };

  const PharmacyLanguages = (): JSX.Element => {
    if (pharmacyInfo?.languages.length) {
      return (
        <div className={styles.PharmacyDetailsInfoFact} data-testid="pharmacy-details-languages">
          <Icon name="message-dots" color={iconColor} />
          <div>
            {pharmacyInfo?.languages.map((language) => {
              return <span key={language}>{language}</span>;
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  const PharmacyHours = (): JSX.Element => {
    return (
      <div data-testid="pharmacy-details-hours">
        {pharmacyInfo?.hours.map((day, index) => {
          return <span key={index}>{day}</span>;
        })}
      </div>
    );
  };

  const showPharmacyDetails = !isLoading && (
    <>
      <PharmacyDetailsPriceCard price={pharmacyParams?.price} />
      <div className={styles.PharmacyDetailsInfo}>
        <Paragraph className={styles.PharmacyDetailsInfoName} testId="pharmacy-details-pharmacy-name">
          {pharmacyInfo?.name}
        </Paragraph>
        <Grid container justifyContent="space-between" testId="pharmacy-details-grid">
          <Grid item colLg={6} colMd={4} colSm={3}>
            <div className={styles.PharmacyDetailsInfoFact}>
              <Icon name="location-standard" color={iconColor} testId="pharmacy-details-address-icon" />
              <div>
                <PharmacyAddress />
              </div>
            </div>
            <div className={styles.PharmacyDetailsInfoFact}>
              <Icon name="phone-landline" color={iconColor} testId="pharmacy-details-phone-icon" />
              <div>{formattedPhone}</div>
            </div>

            <PharmacyLanguages />
          </Grid>
          <Grid item colLg={6} colMd={5} colSm={3}>
            <div className={styles.PharmacyDetailsInfoFact}>
              <Icon name="clock" color={iconColor} testId="pharmacy-details-hours-icon" />

              <PharmacyHours />
            </div>
          </Grid>
        </Grid>
      </div>
    </>
  );

  const loadingSkeleton = isLoading && <Skeleton className={styles.LoadingSkeleton} count={6} />;

  return (
    <div className={styles.PharmacyDetails}>
      <div className={styles.PharmacyDetailsHeader}>
        <Paragraph className={styles.PharmacyDetailsHeaderName} testId="pharmacy-details-header-pharmacy-name">
          {pharmacyInfo?.name}
        </Paragraph>
        <Paragraph className={styles.PharmacyDetailsHeaderDistance} testId="pharmacy-details-header-distance">
          {pharmacyParams?.distance} miles
        </Paragraph>
      </div>
      {showPharmacyDetails}
      {loadingSkeleton}
    </div>
  );
};

export default PharmacyDetailsComponent;
